/**
 * Pure rules of Tacada Limpa: no engine, no clock, no Math.random. A round is 3 holes drawn by the seed from the level's
 * pool (order and cup variant), at most MAX_STROKES per hole. Scores are integers (PROJECT_CONTEXT §5).
 */
import { HOLES, LEVEL_POOLS, holeCourse, type HoleDef } from './holes'
import { distPx, type Course, type Pt } from './physics'
import { createRng } from './random'

export const HOLES_PER_ROUND = 3
export const MAX_STROKES = 6
/** Strokes counted for a hole left unfinished after MAX_STROKES. */
export const UNFINISHED_STROKES = MAX_STROKES + 1
export const BONUS_MAX = 50
/** First-putt bonus loses one point per this many table pixels of distance to the cup. */
export const BONUS_PX_PER_POINT = 6

export interface RoundHole { def: HoleDef; cupVariant: number; gatePhaseMs: number; course: Course }

/** The round of a seed at a level: the same for everyone who gets it (pillar H2). */
export function roundOf(levelId: string, seed: number): RoundHole[] {
  const pool = LEVEL_POOLS[levelId]
  if (!pool) throw new Error(`unknown level "${levelId}"`)
  const rng = createRng(seed)
  const ids = [...pool]
  for (let i = ids.length - 1; i > 0; i--) { const j = rng.int(i + 1); [ids[i], ids[j]] = [ids[j], ids[i]] }
  return ids.slice(0, HOLES_PER_ROUND).map((id) => {
    const def = HOLES[id]
    const cupVariant = rng.int(def.cups.length)
    const gatePhaseMs = rng.int(10_000)
    return { def, cupVariant, gatePhaseMs, course: holeCourse(def, cupVariant, gatePhaseMs) }
  })
}

/** First-putt bonus: 50 for a hole in one, then one point less per 6 px between where the first putt stopped and the cup. */
export function firstPuttBonus(stop: Pt, cup: Pt, holed: boolean): number {
  if (holed) return BONUS_MAX
  return Math.max(0, BONUS_MAX - Math.floor(distPx(stop, cup) / BONUS_PX_PER_POINT))
}

export const PAR_POINTS = 200
export const BOGEY_POINTS = 100
/**
 * Points of a finished hole: 200 at or under par, 100 one over, 0 beyond, plus the first-putt bonus. Flat at par on purpose:
 * a hole in one pays its bonus (50), not double, so the last few percent of precision cannot run away with the score (H10).
 * An unfinished hole scores 0.
 */
export function holePoints(strokes: number, par: number, bonus: number, holed: boolean): number {
  if (!holed) return 0
  return (strokes <= par ? PAR_POINTS : strokes === par + 1 ? BOGEY_POINTS : 0) + bonus
}

export interface HoleScore { id: string; par: number; strokes: number; holed: boolean; bonus: number; points: number }
export interface RoundState { hole: number; strokes: number; firstBonus: number; cards: HoleScore[]; score: number }
export const newRound = (): RoundState => ({ hole: 0, strokes: 0, firstBonus: 0, cards: [], score: 0 })

/** Applies one putt of the current hole. */
export function applyPutt(s: RoundState, round: RoundHole[], stop: Pt, holed: boolean): RoundState {
  const h = round[s.hole]
  const strokes = s.strokes + 1
  const firstBonus = strokes === 1 ? firstPuttBonus(stop, h.course.cup, holed) : s.firstBonus
  if (!holed && strokes < MAX_STROKES) return { ...s, strokes, firstBonus }
  const card: HoleScore = {
    id: h.def.id, par: h.def.par, strokes: holed ? strokes : UNFINISHED_STROKES, holed, bonus: firstBonus,
    points: holePoints(strokes, h.def.par, firstBonus, holed)
  }
  return { hole: s.hole + 1, strokes: 0, firstBonus: 0, cards: [...s.cards, card], score: s.score + card.points }
}

export const roundPar = (round: RoundHole[]) => round.reduce((n, h) => n + h.def.par, 0)
export const totalStrokes = (s: RoundState) => s.cards.reduce((n, c) => n + c.strokes, 0)
export const isOver = (s: RoundState) => s.hole >= HOLES_PER_ROUND
/** Victory of a finished round: total strokes ≤ total par. */
export const isWin = (s: RoundState, round: RoundHole[]) => isOver(s) && totalStrokes(s) <= roundPar(round)
/** Terminal score: the sum of hole points on a victory, 0 on a defeat (family finite). */
export const terminalScore = (s: RoundState, round: RoundHole[]) => (isWin(s, round) ? s.score : 0)
